import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE, installSessionRenewal } from '../services/session';
import { markActivity, signOut } from '../services/idleSession';
import { installMultipart } from '../services/multipart';

// ─── Axios instance ────────────────────────────────────────────────────────────
export const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

// Attach token to every request
api.interceptors.request.use(config => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// W-4: the same renewal as services/api.js (services/session.js) — renew
// once, keep the rotated refresh token, retry; /login only when it fails.
// The login call itself is never intercepted.
installSessionRenewal(api);
// C-3: FormData bodies go out as multipart (services/multipart.js).
installMultipart(api);

// ─── Context ───────────────────────────────────────────────────────────────────
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  // Restore session on mount — NO useNavigate here
  useEffect(() => {
    const token  = localStorage.getItem('access_token');
    const stored = localStorage.getItem('lpms_user');
    if (token && stored) {
      try {
        setUser(JSON.parse(stored));
      } catch {
        localStorage.clear();
      }
    }
    setLoading(false);
  }, []);

  // Login — returns the user object so the caller can navigate
  const login = useCallback(async (email, password) => {
    const response = await api.post('/accounts/login/', { email, password });
    const payload  = response.data;

    let access, refresh, userData;

    if (payload?.data?.tokens) {
      // Shape A: { data: { tokens: { access, refresh }, user } }
      access   = payload.data.tokens.access;
      refresh  = payload.data.tokens.refresh;
      userData = payload.data.user;
    } else if (payload?.data?.access) {
      // Shape B: { data: { access, refresh, user } }
      access   = payload.data.access;
      refresh  = payload.data.refresh;
      userData = payload.data.user;
    } else {
      // Shape C: flat { access, refresh, user }
      access   = payload.access;
      refresh  = payload.refresh;
      userData = payload.user;
    }

    if (!access) throw new Error('Login failed: no token received.');

    localStorage.setItem('access_token',  access);
    localStorage.setItem('refresh_token', refresh);
    localStorage.setItem('lpms_user',     JSON.stringify(userData));
    markActivity({ force: true });   // C7: a new session starts its idle clock
    setUser(userData);

    // Return userData so Login.jsx can navigate based on role
    return userData;
  }, []);

  // Logout (v1.4.1 C7): the refresh token is blacklisted server-side
  // (POST /accounts/logout/) and every other open tab signs out too. The
  // caller navigates (no redirect from here), then the rest of storage goes.
  const logout = useCallback(async () => {
    await signOut('user', { redirect: false });
    localStorage.clear();
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res     = await api.get('/accounts/me/');
      const updated = res.data?.data ?? res.data;
      localStorage.setItem('lpms_user', JSON.stringify(updated));
      setUser(updated);
    } catch {
      // token interceptor handles real 401s
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser, api }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
